import { AuthUtils, AuthResponse } from '../utils/auth';

interface User {
  user_id: string;
  username: string;
  email: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user?: User;
}

interface RegisterResponse {
  success: boolean;
  message: string;
}

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    // Process password with SHA256
    const hashedPassword = await AuthUtils.processPassword(password);
    
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include HTTP-only cookies
      body: JSON.stringify({
        username: username,
        password_hash: hashedPassword
      })
    });
    
    const result = await response.json();
    if (!response.ok) {
      // FastAPI errors provide 'detail'
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || 'Login failed');
    }
    return result as LoginResponse;
  },

  async register(username: string, email: string, password: string): Promise<RegisterResponse> {
    // Process password with SHA256
    const hashedPassword = await AuthUtils.processPassword(password);
    
    const response = await fetch('/api/v1/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: AuthUtils.sanitizeUsername(username),
        email: email.toLowerCase().trim(),
        password_hash: hashedPassword
      })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Registration failed');
    }
    
    return result;
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetch('/api/v1/auth/me', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.user;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  },

  async logout(): Promise<void> {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include'
    });
  }
};
